import { AbsgArrayHelper, LogService } from '@absg/angular-common';
import {
  Rule as FormularyDefaultSetting,
  RuleProductSettingApi,
  RuleProductSettingDetail,
  SupplierProductRuleXrefLite as formularyXrefs,
} from '@absg/api-formulary';
import {
  Rule as StationDefaultSetting,
  RuleLite as ContainerXref,
} from '@absg/api-station-management';
import {
  BaseService, EnvironmentService, FormularyType, RuleContainerSetting, RuleHelper, Rules, RuleService, SettingHelperService, SettingServiceToken,
  StringConstants,
} from '@absg/nim-client-shared';
import { Injectable, OnDestroy } from '@angular/core';
import { StationInfoService } from 'core.services/station-info.service';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SettingsEngine } from 'shared.root/helpers/settings.engine';
import { SubSink } from 'subsink';

import { StationManagementService } from './state-management/station-management.service';

@Injectable({ providedIn: 'root' })
export class SettingsService extends BaseService implements SettingServiceToken, OnDestroy {
  containerXrefs = new BehaviorSubject<ContainerXref[]>(null);
  formularyXrefs = new BehaviorSubject<formularyXrefs[]>(null);
  megFormularyXrefs = new BehaviorSubject<formularyXrefs[]>(null);
  formularyDefaults = new BehaviorSubject<FormularyDefaultSetting[]>(null);
  stationDefaults = new BehaviorSubject<StationDefaultSetting[]>(null);
  stationContainerId: number;
  private subSink = new SubSink();

  constructor(
    loggingService: LogService,
    private formularyXrefSettingApi: RuleProductSettingApi,
    private stationInfoService: StationInfoService,
    private ruleService: RuleService,
    private settingHelperService: SettingHelperService,
    private stationManagementService: StationManagementService,
    private environmentService: EnvironmentService,
  ) {
    super(loggingService);
    this.subSink.sink = this.stationInfoService.station.subscribe(() => {
      this.populateDefaults(this.stationInfoService.getStationId());
    });
  }

  getCanPrintLabels(): boolean {
    return this.environmentService.environmentConfig?.isLabelPrinterAutomatic ?? true;
  }

  getCanPrintReceipts(): boolean {
    return this.environmentService.environmentConfig?.isReceiptPrinterAutomatic ?? true;
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  populateDefaults(stationId: number): void {
    this.stationContainerId = stationId;
    if (stationId === 0) {
      return; // skip loading rules for a null station
    }
    // TODO: replace stationDefaults with new load call. (#162616)
    forkJoin(
      [this.ruleService.getStationDefaultRules(),
      this.ruleService.getFormularyDefaultRules()],
    ).pipe(take(1))
      .subscribe((results) => {
        /* istanbul ignore else  */
        if (results) {
          this.stationDefaults.next(results[0]);
          this.formularyDefaults.next(results[1]);
        }
      });
  }

  populateXrefs(containerIds: number[], getSpecificFormularyItems: number[] = []): Observable<void> {
    const getXrefsByContainerIds = containerIds.length === 0 ?
      of([]) :
      this.stationManagementService.getXrefsByContainers(containerIds);
    const getXrefsForItems = getSpecificFormularyItems.length === 0
      ? this.formularyXrefSettingApi.getProductXrefs()
      : this.formularyXrefSettingApi.getAssignedProductXrefs(getSpecificFormularyItems);

    const getBinXrefItem = (getSpecificFormularyItems.length > 0) ?
      this.formularyXrefSettingApi.getBinProductXrefs(getSpecificFormularyItems) :
      of([]);

    return forkJoin([
      getXrefsForItems,
      getXrefsByContainerIds,
      getBinXrefItem,
    ]).pipe(take(1),
      map((results) => {
        /* istanbul ignore else  */
        if (results) {
          this.formularyXrefs.next(results[0]);
          this.containerXrefs.next(results[1]);
          // TODO:Populate megFormularyXrefs when practice formulary created
          this.megFormularyXrefs.next(results[2]);
        }
      }),
    );
  }

  getAutoReOrderSetting(containerId: number, formularyId: number, formularyType: FormularyType, isPatientOwned = false): boolean {
    return this.settingHelperService.getAutoReOrderSetting(containerId, formularyId, formularyType,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue(),
      isPatientOwned);
  }

  populateItemAndBinXrefs(supplierProductInclusionId: number): Observable<void> {
    const getFormularyXrefItem = this.formularyXrefSettingApi.getRulesForSupplierProducts([supplierProductInclusionId]);
    const getBinXrefItem = this.formularyXrefSettingApi.getBinProductXrefs([supplierProductInclusionId]);

    return forkJoin([
      getFormularyXrefItem,
      getBinXrefItem,
    ]).pipe(take(1),
      map(([formularyCrossRefs, binCrossRefs]) => {
        if (formularyCrossRefs) {
          const newFormularyXrefs = this.mapRuleProductSetting(formularyCrossRefs);
          const formXrefs = this.formularyXrefs.getValue();
          this.formularyXrefs.next(formXrefs.concat(newFormularyXrefs));
        }
        if (binCrossRefs) {
          const megXRefs = this.megFormularyXrefs.getValue();
          this.megFormularyXrefs.next(megXRefs.concat(binCrossRefs));
        }
      }),
    );
  }

  private mapRuleProductSetting(ruleProductSettings: RuleProductSettingDetail[]): formularyXrefs[] {
    return AbsgArrayHelper.map(ruleProductSettings[0].ruleProductSettings, ruleProductSetting => {
      const formXRef = {
        ruleId: ruleProductSetting.ruleId,
        value: ruleProductSetting.xrefValue,
        supplierProductId: ruleProductSetting.supplierProductId,
        supplierProductInclusionId: ruleProductSetting.supplierProductId,
        lastModifiedDate: ruleProductSetting.xrefTime,
      } as formularyXrefs;
      return formXRef;
    });
  }

  getAutoReorderSettingByBinValue(containerId: number, formularyId: number, formularyType: FormularyType, isPatientOwned = false, isBin = true): boolean {
    if (formularyType === FormularyType.PracticeOwnedItem || isPatientOwned) { return false; }

    const containerAutoReorderSetting = AbsgArrayHelper.find(this.containerXrefs.getValue(), xref =>
      xref.ruleId === Rules.positionLevelRules.autoReorder && xref.containerId === containerId);

    if (containerAutoReorderSetting) {
      return RuleHelper.getBooleanFromText(containerAutoReorderSetting.xrefValue);
    }

    let formularyAutoReorderSetting: formularyXrefs = null;
    if (isBin) {
      formularyAutoReorderSetting = AbsgArrayHelper.find(this.megFormularyXrefs.getValue(), xref =>
        xref.ruleId === Rules.positionLevelRules.autoReorder && xref.supplierProductInclusionId === formularyId);
    } else {
      formularyAutoReorderSetting = AbsgArrayHelper.find(this.formularyXrefs.getValue(), xref =>
        xref.ruleId === Rules.positionLevelRules.autoReorder && xref.supplierProductInclusionId === formularyId);
    }

    if (formularyAutoReorderSetting) {
      return RuleHelper.getBooleanFromText(formularyAutoReorderSetting.value);
    }

    const formularyDefaultAutoReorderSetting = AbsgArrayHelper.find(this.formularyDefaults.getValue(), rule =>
      rule.ruleId === Rules.positionLevelRules.autoReorder);

    return RuleHelper.getBooleanFromText(formularyDefaultAutoReorderSetting.defaultValue);
  }

  getAutoPrintLabelSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintLabels()) { return false; };
    return this.settingHelperService.getAutoPrintLabelSetting(containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getLotTrackingSetting(containerId: number): boolean {
    return this.getItemSetting(Rules.positionLevelRules.lotTracking, containerId);
  }

  getReasonForReturnSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForReturnSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReasonForWasteSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForWasteSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReasonForPackingSlipRestockSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService
      .getReasonForPackingSlipRestockSetting(
        containerId,
        formularyId,
        this.containerXrefs.getValue(),
        this.formularyDefaults.getValue(),
        this.formularyXrefs.getValue(),
        this.stationDefaults.getValue(),
        this.stationContainerId,
        this.megFormularyXrefs.getValue());
  }

  getReasonForPackingSlipRestockRequireBothSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForPackingSlipRestockRequireBothSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReasonForMarkClosedSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService
      .getReasonForMarkClosedSetting(
        containerId,
        formularyId,
        this.containerXrefs.getValue(),
        this.formularyDefaults.getValue(),
        this.formularyXrefs.getValue(),
        this.stationDefaults.getValue(),
        this.stationContainerId,
        this.megFormularyXrefs.getValue());
  }

  getReasonForMarkClosedRequireBothSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForMarkClosedRequireBothSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReasonForStandardRestockSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService
      .getReasonForStandardRestockSetting(
        containerId,
        formularyId,
        this.containerXrefs.getValue(),
        this.formularyDefaults.getValue(),
        this.formularyXrefs.getValue(),
        this.stationDefaults.getValue(),
        this.stationContainerId,
        this.megFormularyXrefs.getValue());
  }

  getReasonForStandardRestockRequireBothSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForStandardRestockRequireBothSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReceiptForRestockSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintReceipts()) { return false; };
    return this.settingHelperService.getReceiptForRestockSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReceiptForReturnSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintReceipts()) { return false; };
    return this.settingHelperService.getReceiptForReturnSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReceiptForWasteSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintReceipts()) { return false; };
    return this.settingHelperService.getReceiptForWasteSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReasonForDiscrepancySetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService
      .getReasonForDiscrepancySetting(
        containerId,
        formularyId,
        this.containerXrefs.getValue(),
        this.formularyDefaults.getValue(),
        this.formularyXrefs.getValue(),
        this.stationDefaults.getValue(),
        this.stationContainerId,
        this.megFormularyXrefs.getValue());
  }

  getReasonForDiscrepancyRequireBothSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForDiscrepancyRequireBothSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReasonForClinicChargeSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForClinicChargeSetting(
      containerId,
      formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReasonForClinicChargeRequireBothSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getPropertySetting(
      Rules.positionLevelRules.reasonForClinicChargeRequireBoth, containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getMinSetting(containerId: number): number {
    return this.settingHelperService.getMinSetting(containerId, this.stationDefaults.getValue(), this.containerXrefs.getValue());
  }

  getMaxSetting(containerId: number): number {
    return this.settingHelperService.getMaxSetting(containerId, this.stationDefaults.getValue(), this.containerXrefs.getValue());
  }

  getReceiptForDispenseSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintReceipts()) { return false; };
    return this.settingHelperService.getReceiptForDispenseSetting(containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getDispenseTypeIdSetting(containerId: number): number {
    return this.settingHelperService.getDispenseTypeIdSetting(containerId, this.stationDefaults.getValue(), this.containerXrefs.getValue());
  }

  getBinSetting(containerId: number): boolean {
    return this.settingHelperService.getBinSetting(containerId, this.stationDefaults.getValue(), this.containerXrefs.getValue());
  }

  getWitnessRequiredSetting(containerId: number, formularyId: number): boolean {

    const isWitness = this.settingHelperService.getWitnessRequiredSetting(
      containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());

    return isWitness;
  }

  getPreferredItemIdSetting(containerId: number): string {
    return this.settingHelperService.getPreferredItemIdSetting(containerId,
      this.stationDefaults.getValue(), this.containerXrefs.getValue());
  }

  getWitnessRequiredStationSetting(): boolean {
    return this.settingHelperService.getWitnessRequiredStationSetting(this.stationContainerId,
      this.stationDefaults.getValue(), this.containerXrefs.getValue());
  }

  getVerifyQOHSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getPropertySetting(
      Rules.positionLevelRules.verifyQOH, containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getPatientHistoryWindowSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getPropertySetting(
      Rules.stationLevelRules.patientHistoryWindow, containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReceiptForClinicChargeSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintReceipts()) { return false; };
    return this.settingHelperService.getReceiptForClinicChargeSetting(
      containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getEnableRequiredNotesSetting(formularyId: number): boolean {
    return RuleHelper.getBooleanFromText(this.settingHelperService.getSettingForFormularyItem(Rules.requiredNotes.EnableRequiredNotes, formularyId,
      this.formularyDefaults.getValue(), this.formularyXrefs.getValue()));
  }

  getRetainAttendingPhysicianSetting(): boolean {
    return RuleHelper.getBooleanFromText(
      this.settingHelperService.getSettingForStation(Rules.stationLevelRules.retainAttendingPhysician, this.stationDefaults.getValue(), this.containerXrefs.getValue(), this.stationContainerId));
  }

  getBillingRequiredSetting(formularyId: number): boolean {
    const tempBillingFormulary = AbsgArrayHelper.find(this.formularyDefaults.getValue(), t => t.ruleId === Rules.formularyRules.billingRequired);
    const tempBillingFormularyXref = AbsgArrayHelper.find(this.formularyXrefs.getValue(), t => t.ruleId === Rules.formularyRules.billingRequired);
    const tempBillingStationDefaults = AbsgArrayHelper.find(this.stationDefaults.getValue(), t => t.ruleId === Rules.stationLevelRules.billingRequired);
    const tempBillingStationXref = AbsgArrayHelper.find(this.containerXrefs.getValue(), t => t.ruleId === Rules.stationLevelRules.billingRequired);
    this.handleMessage(`Billing Required => Formulary = ${JSON.stringify(tempBillingFormulary)} ,
    Formulary Xref = ${JSON.stringify(tempBillingFormularyXref)}
    Station = ${JSON.stringify(tempBillingStationDefaults)}
    StationXref = ${JSON.stringify(tempBillingStationXref)}`);
    const boolBillingRequired = this.settingHelperService.getBillingRequiredSetting(formularyId, this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(), this.stationDefaults.getValue(), this.containerXrefs.getValue(), this.stationContainerId);
    this.handleMessage(`Billing Required => Value Returned from setting service = ${JSON.stringify(boolBillingRequired)}`);
    return boolBillingRequired;
  }

  getPoEmailRecipientListSetting(): string {
    return this.settingHelperService.getPoEmailRecipientListSetting(this.containerXrefs.getValue(), this.stationDefaults.getValue(), this.stationContainerId);
  }

  getReasonForTransferDiscrepancySetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForTransferDiscrepancySetting(
      containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReasonForTransferDiscrepancyRequireBothSetting(containerId: number, formularyId: number): boolean {
    return this.settingHelperService.getReasonForTransferDiscrepancyRequireBothSetting(
      containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReceiptForCancelTransferSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintReceipts()) { return false; };
    return this.settingHelperService.getReceiptForCancelTransferSetting(
      containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getReceiptForTransferInSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintReceipts()) { return false; };
    return this.settingHelperService.getReceiptForTransferInSetting(
      containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getInfusionNoteOnLabel(supplierProductInclusionId: number): boolean {
    const ruleValue = this.getFormularyXrefValue(Rules.formularyRules.printInfuseNotes, supplierProductInclusionId);
    return this.printLabelCheck(ruleValue);
  }

  getInfusionNoteOnReceipt(supplierProductInclusionId: number): boolean {
    const ruleValue = this.getFormularyXrefValue(Rules.formularyRules.printInfuseNotes, supplierProductInclusionId);
    return this.printReceiptCheck(ruleValue);
  }

  getRequiredNoteOnReceipt(supplierProductInclusionId: number): boolean {
    const ruleValue = this.getFormularyXrefValue(Rules.formularyRules.printRequiredNotes, supplierProductInclusionId);
    return this.printReceiptCheck(ruleValue);
  }

  getRequiredNoteOnLabel(supplierProductInclusionId: number): boolean {
    const ruleValue = this.getFormularyXrefValue(Rules.formularyRules.printRequiredNotes, supplierProductInclusionId);
    return this.printLabelCheck(ruleValue);
  }

  getOverridePriceSetting(supplierProductInclusionId: number): boolean {
    return this.settingHelperService.getOverridePriceSetting(
      supplierProductInclusionId,
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue());
  }

  getOverridePriceValue(supplierProductInclusionId: number): number {
    return this.settingHelperService.getOverridePriceValue(
      supplierProductInclusionId,
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue());
  }

  getReceiptForTransferOutSetting(containerId: number, formularyId: number): boolean {
    if (!this.getCanPrintReceipts()) { return false; };
    return this.settingHelperService.getReceiptForTransferOutSetting(
      containerId, formularyId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId,
      this.megFormularyXrefs.getValue());
  }

  getFinalPOApprovalSetting(): boolean {
    return this.settingHelperService.getFinalPOApprovalSetting(
      this.containerXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId);
  }

  convertToInternalModals(defaults: StationDefaultSetting[]): RuleContainerSetting[] {
    return this.settingHelperService.convertToInternalModals(defaults,
      this.containerXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.stationContainerId);
  }

  // Determines Property Setting for a Formulary Item
  getSettingForFormularyItem(ruleId: number, supplierProductInclusionId: number): string {
    return this.settingHelperService.getSettingForFormularyItem(
      ruleId,
      supplierProductInclusionId,
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue());
  }

  // Determines Property Setting of item (assigned or not based on given containerId)
  getPropertySettingWithoutStation(ruleId: number, containerId: number, supplierProductInclusionId: number): boolean {
    return this.settingHelperService.getPropertySettingWithoutStation(
      ruleId,
      containerId,
      supplierProductInclusionId,
      this.containerXrefs.getValue(),
      this.formularyDefaults.getValue(),
      this.formularyXrefs.getValue(),
      this.stationDefaults.getValue(),
      this.megFormularyXrefs.getValue());
  }

  // Determines Item Setting of item (assigned or not based on given containerId)
  getItemSetting(ruleId: number, containerId: number): boolean {
    const stationSetting = this.getStationXrefSetting(ruleId);
    // If not assigned, positionSetting will return undefined and will be treated as no xref found.
    const positionSetting = this.getContainerXrefSetting(ruleId, containerId);
    const defaultValue = this.getStationDefaultSetting(ruleId);
    return SettingsEngine.determineLotTrackingSetting(stationSetting, positionSetting,
      this.checkDefaultValue(defaultValue));
  }

  ruleContainerAdjustment(rule: RuleContainerSetting, supplierProductInclusionId: number): void {
    // Currently only Lot Tracking uses item settings. But Auto Reorder is coming back as item type.
    // Leaving this as this check until we have another rule that follows Item Settings rule.
    if (rule.ruleId === Rules.positionLevelRules.lotTracking) {
      const value = this.getItemSetting(rule.ruleId, rule.containerId);
      rule.value = value ? StringConstants.boolY : StringConstants.boolN;
      rule.originalValue = rule.value;
      rule.booleanValue = value;
    } else {
      // Calling without station since these are use for value and for properties station is use for enabled value.
      const value = this.getPropertySettingWithoutStation(rule.ruleId, rule.containerId, supplierProductInclusionId);
      rule.value = value ? StringConstants.boolY : StringConstants.boolN;
      rule.originalValue = rule.value;
      rule.booleanValue = value;
    }
  }

  updateContainerXrefSetting(containerId: number, ruleId: number, value: string): void {
    const xrefs = this.containerXrefs.getValue();
    const xrefFound = AbsgArrayHelper.find(xrefs, xref => xref.ruleId === ruleId && xref.containerId === containerId);
    if (!AbsgArrayHelper.isNil(xrefFound)) {
      xrefFound.xrefValue = value;
    }
  }

  getBinGroupWitnessRequiredValue(ruleId: number, supplierProductInclusionId: number): boolean {
    const witnessRule = this.getMEGFormularyXrefSetting(ruleId, supplierProductInclusionId);
    if (!AbsgArrayHelper.isNil(witnessRule)) {
      return RuleHelper.getBooleanFromText(witnessRule.value);
    }
    return false;
  }

  // Getting bin group preferred value
  getPreferredItemIdBinGroupSetting(supplierProductInclusionId: number): string {
    return this.settingHelperService.getPreferredItemIdBinGroupSetting(supplierProductInclusionId, this.megFormularyXrefs.getValue());
  }

  getUnassignedItemRuleValue(ruleId: number, isDefault: boolean, formularyXref: string = null): boolean {
    let stationRuleValue: boolean;
    let formularyRuleValue: boolean;

    const stationId = this.stationInfoService.getStationId();
    const stationXrefs = this.containerXrefs.getValue();
    const stationXrefValue = AbsgArrayHelper.find(stationXrefs, x => x.ruleId === ruleId && x.containerId === stationId)?.xrefValue;
    if (AbsgArrayHelper.isNil(stationXrefValue)) {
      const stationDefaults = this.stationDefaults.getValue();
      stationRuleValue = RuleHelper.getBooleanFromText(AbsgArrayHelper.find(stationDefaults, x => x.ruleId === ruleId)?.defaultValue);
    } else {
      stationRuleValue = RuleHelper.getBooleanFromText(stationXrefValue);
    }

    if (isDefault) {
      const formularyDefaults = this.formularyDefaults.getValue();
      formularyRuleValue = RuleHelper.getBooleanFromText(AbsgArrayHelper.find(formularyDefaults, x => x.ruleId === ruleId)?.defaultValue);
    } else {
      formularyRuleValue = RuleHelper.getBooleanFromText(formularyXref);
    }

    if (stationRuleValue && formularyRuleValue) {
      return true;
    }
    return false;
  }

  private getMEGFormularyXrefSetting(ruleId: number, supplierProductInclusionId: number): formularyXrefs {
    const xrefs = this.megFormularyXrefs.getValue();
    return AbsgArrayHelper.find(xrefs, xref => xref.ruleId === ruleId && xref.supplierProductInclusionId === supplierProductInclusionId);
  }

  private getFormularyXrefSetting(ruleId: number, supplierProductInclusionId: number): formularyXrefs {
    const xrefs = this.formularyXrefs.getValue();
    return AbsgArrayHelper.find(xrefs, xref => xref.ruleId === ruleId && xref.supplierProductInclusionId === supplierProductInclusionId);
  }

  private getStationXrefSetting(ruleId: number): ContainerXref {
    return this.getContainerXrefSetting(ruleId, this.stationContainerId);
  }

  private getContainerXrefSetting(ruleId: number, containerId: number): ContainerXref {
    const xrefs = this.containerXrefs.getValue();
    return AbsgArrayHelper.find(xrefs, xref => xref.ruleId === ruleId && xref.containerId === containerId);
  }

  private getFormularyXrefValue(ruleId: number, supplierProductInclusionId: number): string {
    const foundXref = this.getFormularyXrefSetting(ruleId, supplierProductInclusionId);
    const defaultValue = this.getFormularyDefaultSetting(ruleId);
    return SettingsEngine.determineSettingFromFormulary(foundXref, this.checkDefaultValue(defaultValue));
  }

  private getFormularyDefaultSetting(ruleId: number): FormularyDefaultSetting {
    const defaults = this.formularyDefaults.getValue();
    return AbsgArrayHelper.find(defaults, setting => setting.ruleId === ruleId);
  }

  private checkDefaultValue(defaultValue: FormularyDefaultSetting | StationDefaultSetting): string {
    return !AbsgArrayHelper.isNil(defaultValue) ? defaultValue.defaultValue : 'N';
  }

  private printLabelCheck(value: string): boolean {
    const upValue = value.toUpperCase();
    return upValue === Rules.formularyRules.constForLabel || upValue === Rules.formularyRules.constForBoth;
  }

  private printReceiptCheck(value: string): boolean {
    const upValue = value.toUpperCase();
    return upValue === Rules.formularyRules.constForReceipt || upValue === Rules.formularyRules.constForBoth;
  }

  private getStationDefaultSetting(ruleId: number): StationDefaultSetting {
    const defaults = this.stationDefaults.getValue();
    return AbsgArrayHelper.find(defaults, setting => setting.ruleId === ruleId);
  }
}
